"use client";

// Lazily-reused 2D context for normalizing CSS colors via the browser parser.
let scratch: CanvasRenderingContext2D | null = null;
function ctx2d(): CanvasRenderingContext2D | null {
  if (!scratch) scratch = document.createElement("canvas").getContext("2d");
  return scratch;
}

export interface ParsedColor {
  /** #rrggbb */
  hex: string;
  /** 0..1 */
  alpha: number;
}

/**
 * Parse any CSS color string (hex, rgb/rgba, named) into a `#rrggbb` hex plus an
 * alpha channel, using the browser's own parser. The caption style mixes hex and
 * rgba values, so this gives a uniform basis for the hex color picker + a
 * separate opacity control.
 */
export function parseColor(input: string): ParsedColor {
  const ctx = ctx2d();
  if (!ctx) return { hex: "#000000", alpha: 1 };
  ctx.fillStyle = "#000000";
  ctx.fillStyle = input;
  const normalized = ctx.fillStyle; // "#rrggbb" or "rgba(r, g, b, a)"
  if (normalized.startsWith("#")) {
    return { hex: normalized.length === 7 ? normalized : "#000000", alpha: 1 };
  }
  const m = normalized.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!m) return { hex: "#000000", alpha: 1 };
  const toHex = (n: string | undefined) =>
    Number(n ?? 0)
      .toString(16)
      .padStart(2, "0");
  return {
    hex: `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`,
    alpha: m[4] !== undefined ? Number(m[4]) : 1,
  };
}

/** Compose a `#rrggbb` hex and an alpha (0..1) into an `rgba(...)` string. */
export function rgbaString(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * 100) / 100})`;
}
